/**
 * UnifiedInboxSurface — the consumption rendering for a single unified inbox,
 * extracted from the old `/admin/unified-inbox` page (FE-02).
 *
 * Renders the banner → stats → filters → table → thread → compose stack for
 * one inbox. Does NOT include the MailboxSwitcher (the parent page owns that),
 * the setup wizard (moved to the admin tab), or the denied/empty states (the
 * parent page's selection logic guarantees a valid, accessible inbox).
 *
 * URL contract: filters are URL-synced via the existing InboxFiltersBar codec.
 * The parent page owns the `?inbox=` param; this component reads/writes the
 * filter params (search, sender, replied, date, sort, direction, page) and the
 * rail folder (`?inbox_folder=`, see `inboxDeepLink.js`) on the same query
 * string.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import InboxFiltersBar, {
    filtersFromSearch,
    searchFromFilters,
} from './InboxFiltersBar';
import ImportStatusBanner from './ImportStatusBanner';
import UnifiedInboxTable, { InboxEmptyState } from './UnifiedInboxTable';
import { emptyFolderCopy } from './emptyFolderCopy';
import { ConnectedFolderRail } from './FolderRail';
import { useBulkTagPanel } from './BulkTagPanel';
import TrashNoticeBar from './TrashNoticeBar';
import { draftIdsOf, partitionDrafts } from './draftRows';
import { mailStateToggles } from './mailRowState';
import { dispatchBulkStateAction } from './bulkDispatch';
import { useDeleteDrafts } from '../../hooks/useMailDrafts';
import useMailSelection from '../../hooks/useMailSelection';
import {
    useArchiveMail,
    useAssignMail,
    useMoveMail,
    useReadMail,
    useSnoozeMail,
    useImportantMail,
    useStarMail,
    resolveSnoozePreset,
} from '../../hooks/useMailState';
import { resolveDisplayZone } from '../../lib/timezone';
import { atLeast, canReply, roleFor } from './inboxRules';
import {
    useBulkDeleteInboxMessages,
    useEmptyInboxTrash,
    usePurgeInboxMessage,
    useRestoreInboxMessage,
    useDeleteInboxMessage,
    useInboxMessages,
    useInboxStats,
} from '../../hooks/useUnifiedInbox';
import { useMailFolders } from '../../hooks/useMailFolders';
import {
    DEFAULT_INBOX_FOLDER,
    INBOX_FOLDER_PARAM,
    railViewIds,
    resolveInboxFolder,
    searchWithFolder,
} from './inboxDeepLink';

/**
 * @param {object} props
 * @param {object} props.selectedInbox  — the inbox object from the grant list
 * @param {object} props.user           — the current user from AuthContext
 * @param {function} props.getAuthHeader — from AuthContext
 * @param {boolean} [props.isAdmin]     — whether the user is an admin (settings link,
 *                                        tag management)
 *
 * No Re-verify here: the subscription banner and its Re-verify control live on
 * Admin › Unified Inbox only (removed from the mail list by b0fec705a).
 */
export default function UnifiedInboxSurface({
    selectedInbox,
    user,
    getAuthHeader,
    isAdmin,
    // Issue #662: threaded to InboxFiltersBar → MailboxFilterSelect's
    // "Manage mailboxes" footer. Absent on both = no footer button.
    canManage = false,
    onManageMailboxes,
}) {
    const navigate = useNavigate();
    const location = useLocation();

    const inboxId = selectedInbox?.inbox_id;
    const role = roleFor(selectedInbox, user);

    // ── URL filter state ─────────────────────────────────────────────────
    const urlFilters = useMemo(
        () => filtersFromSearch(location.search),
        [location.search]
    );

    // ── The rail's folder, from the URL ──────────────────────────────────
    //
    // The rail and the list read the SAME folder id, and the bulk predicate is
    // built from the same view — one vocabulary
    // (`services/mail_folders.py::folder_clauses`) serves the rail's counts,
    // the list's rows and a bulk action over them, so a view cannot count one
    // set and act on another.
    //
    // The folder is `?inbox_folder=`, derived on every render and never copied
    // into state: a reload, a shared link and Back from the reader (which
    // unmounts this surface) all return to it, and a click on another inbox in
    // the switcher — a fresh `/inbox?inbox=<id>` — drops it by construction.
    // The rail's model is read only to check the URL's value against what the
    // rail draws (one request: the rail asks for the same query). While it has
    // not answered the URL is trusted, so a bell link opens Trash without
    // waiting on the rail's counts.
    const { data: railModel } = useMailFolders(inboxId);
    const viewIds = useMemo(() => railViewIds(railModel), [railModel]);
    const folder = useMemo(
        () => resolveInboxFolder(location.search, viewIds),
        [location.search, viewIds]
    );
    // What `searchFromFilters` must carry: it rebuilds the whole query string,
    // so a folder it was not told about would be dropped by every filter change.
    const folderParam = useMemo(
        () => (folder !== DEFAULT_INBOX_FOLDER ? { [INBOX_FOLDER_PARAM]: folder } : {}),
        [folder]
    );

    // What goes to the API — everything except the inbox id, which is a path
    // segment rather than a filter.
    //
    // `sort` and `direction` used to be stripped here, because sorting was done
    // in the browser over whatever pages had accumulated. That could only ever
    // order the rows already loaded: the server returned the NEWEST rows and the
    // browser sorted those, so "sort by subject" meant "the alphabetically first
    // of the most recent 25" and the answer changed every time you scrolled. The
    // server sorts now, so these must reach it.
    const filters = useMemo(() => {
        const { inbox: _inbox, ...rest } = urlFilters;
        return rest;
    }, [urlFilters]);

    // The bar renders the sort controls, so it needs the same object.
    const barFilters = filters;

    const setFilters = useCallback(
        next => {
            // `next` wins over the URL for sort and direction — that is how the
            // bar's own controls take effect. Overriding them with `urlFilters`
            // unconditionally, as this did, meant every write replayed the value
            // already in the URL: the direction select could be changed and the
            // list never moved. `??`, not `||`, so an explicit 'desc' is honoured
            // rather than falling through to the previous value.
            const { sort, direction, ...rest } = next;
            navigate(
                {
                    pathname: location.pathname,
                    search: searchFromFilters(
                        {
                            ...rest,
                            sort: sort ?? urlFilters.sort,
                            direction: direction ?? urlFilters.direction,
                        },
                        { inbox: inboxId, ...folderParam }
                    ),
                },
                { replace: true }
            );
        },
        [location.pathname, navigate, inboxId, folderParam, urlFilters.direction, urlFilters.sort]
    );

    const toggleSort = useCallback(
        key => {
            const direction =
                urlFilters.sort === key && urlFilters.direction !== 'asc' ? 'asc' : 'desc';
            navigate(
                {
                    pathname: location.pathname,
                    search: searchFromFilters(
                        { ...filters, sort: key, direction },
                        { inbox: inboxId, ...folderParam }
                    ),
                },
                { replace: true }
            );
        },
        [filters, location.pathname, navigate, inboxId, folderParam, urlFilters.direction, urlFilters.sort]
    );

    // ── Data hooks ────────────────────────────────────────────────────────
    const pageSize = filters.pageSize || 25;

    // ── Pagination state ─────────────────────────────────────────────────
    //
    // Deliberately component state and NOT the URL. Scrolling used to write
    // `page=7` into the address bar, so reloading — or sharing the link —
    // landed on page 7 alone with an empty accumulator: six pages of mail
    // missing, and nothing on screen saying so. Where a reader has scrolled to
    // is not a bookmarkable view; the FILTERS are, and those stay in the URL.
    //
    // `cursor` is the server's resume point. It falls back to `page` for the
    // conversation-level sorts, which have no row position to resume from.
    const [cursor, setCursor] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    // A rail click writes the folder to the URL; the surface re-renders on it.
    // `replace`, like every filter write: a folder is not a place in history,
    // and pushing one per click would make Back walk the rail instead of
    // leaving it. Re-selecting the current folder writes nothing.
    const selectFolder = useCallback(
        next => {
            const search = searchWithFolder(location.search, next);
            if (search === location.search) return;
            navigate({ pathname: location.pathname, search }, { replace: true });
        },
        [location.pathname, location.search, navigate]
    );
    // #646 — the rail's tag filter: a tag_id, composed with the folder (not a
    // folder of its own). Component state, unlike `folder`: it is not in the
    // URL, so Back from the reader and a reload drop it.
    const [tag, setTag] = useState(null);
    // A tag is picked from ONE inbox's rail; another inbox's rail may not list
    // it, which would leave a filter on screen with no control to lift it.
    useEffect(() => { setTag(null); }, [inboxId]);

    // `folder` belongs in the QUERY, not just in the rail's highlight. Without
    // it the rail lit up the view you clicked and the list kept showing Inbox:
    // the backend has accepted `folder` since `_mail_folder_clauses` landed,
    // and nothing on this lane ever sent it. It also has to be part of the
    // cache key, or Starred and Inbox share one entry and the second click
    // renders the first one's rows.
    const pagedFilters = useMemo(
        () => ({ ...filters, folder, tag, page: currentPage, cursor }),
        [filters, folder, tag, currentPage, cursor]
    );
    const {
        data: messages,
        isLoading: messagesLoading,
        isFetching: messagesFetching,
        // True while the query is showing the PREVIOUS page's data because the
        // next one is still in flight (`placeholderData: previous => previous`).
        // Reading that as this page's answer is how the no-progress guard once
        // retired the scroll on the very first load-more.
        isPlaceholderData: messagesArePlaceholder,
        error: messagesError,
        refetch: refetchMessages,
    } = useInboxMessages(inboxId, pagedFilters);
    const { data: stats, refetch: refetchStats } = useInboxStats(inboxId, {
        mailbox: filters.mailbox || '',
    });

    const handleRefresh = useCallback(() => {
        refetchMessages();
        refetchStats();
    }, [refetchMessages, refetchStats]);

    const [pendingDelete, setPendingDelete] = useState(null);
    // The bulk Trash verbs confirm through the row path's AlertDialog (see
    // `requestBulkTrash`), so this carries what the user confirmed: the
    // snapshotted ids and which door they asked for.
    const [pendingTrash, setPendingTrash] = useState(null);

    const deleteMessage = useDeleteInboxMessage(inboxId);

    // ── Folder + selection ────────────────────────────────────────────────
    //
    const bulkDelete = useBulkDeleteInboxMessages(inboxId);
    // Trash on this lane is the per-inbox removal record — see
    // `services/mail_folders.inbox_trash_clauses`. Delete WRITES that record
    // and is therefore the move; these are the two doors out of the bin and
    // the bulk empty.
    const restoreMessage = useRestoreInboxMessage(inboxId);
    const purgeMessage = usePurgeInboxMessage(inboxId);
    const emptyTrash = useEmptyInboxTrash(inboxId);
    // Drafts are not in `communications`, so this lane's per-message delete
    // answers 404 for one — see `draftRows.js`.
    const deleteDrafts = useDeleteDrafts(inboxId);
    const [confirmEmptyTrash, setConfirmEmptyTrash] = useState(false);

    // ── Compose options ──────────────────────────────────────────────────
    const ownAddress = user?.email;
    const mayCompose = useMemo(
        () => canReply(selectedInbox, user, { ownAddress }),
        [ownAddress, selectedInbox, user]
    );

    // ── Pagination: accumulate entries across pages ──────────────────────
    // The hook sends `page`/`page_size` to the backend, which returns one page.
    // `placeholderData: previous => previous` keeps the prior page visible
    // while the next loads, but we need to APPEND rows, not replace them. So we
    // accumulate rows in local state, deduplicating by comm_id, and reset the
    // accumulator whenever a non-page filter changes (so "Load more" does not
    // trigger a reset, but changing the search does).
    const totalCount = messages?.total || 0;
    // `totalCount` is exact, and describes the FILTERED list. It counts
    // conversations by the same stored `conversation_key` the page groups on,
    // and the `replied` filter is applied to both — so "N of M shown" is a fact
    // about the list on screen. It used to be neither: the count grouped by
    // `internet_message_id` alone while the collapse used four edges, and the
    // reply filter ran in Python after the count, which is how the unified
    // inbox came to render two rows under the heading "2 of 453 shown".
    //
    // `hasMore` remains the authoritative "are there more pages?" signal rather
    // than `totalCount > entries.length`: a list that grows while it is being
    // read moves the target, and the backend knows whether its own cursor has
    // reached the end.
    const hasMore = messages?.has_more ?? false;

    const [allRows, setAllRows] = useState([]);

    // Every comm_id currently on screen.
    //
    // The dedupe is belt and braces now rather than load-bearing. It used to be
    // the only thing standing between the reader and a duplicated row, because
    // pagination counted RAW ROWS while pages returned collapsed ENTRIES: a page
    // could re-serve most of the previous window, and at the end of a pooled
    // mailbox it could return nothing new at all while `has_more` stayed true.
    // The server pages over conversations now, so a conversation appears on
    // exactly one page and `has_more` is a fact rather than an estimate — but a
    // list that grows while it is being read can still deliver the same row
    // twice, and rendering it twice is worse than a `Set` lookup.
    const seenIdsRef = useRef(new Set());

    // Reset the accumulator when inbox, folder or non-page filters change.
    // `filterKey` deliberately excludes `page` so "Load more" (which bumps
    // page) does not wipe the accumulated rows.
    //
    // `folder` belongs here for the same reason everything else does: without
    // it, switching folders left the previous folder's rows on screen until the
    // new query resolved — a window in which the list showed mail the rail says
    // is somewhere else. The personal surface had the mirror-image defect (it
    // cleared from a handler, so re-selecting the CURRENT folder emptied the
    // list permanently); both lanes now derive the reset from the query.
    const filterKey = `${inboxId}:${folder}:${filters.search}:${filters.sender}:${filters.dateFrom}:${filters.dateTo}:${filters.replied}:${filters.sort}:${filters.direction}:${filters.mailbox || ''}:${tag || ''}`;
    useEffect(() => {
        setAllRows([]);
        seenIdsRef.current = new Set();
        setCursor(null);
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey]);

    // Accumulate rows as each page's data arrives. Page 1 replaces (handles the
    // reset above and fresh loads); later pages append with comm_id dedupe.
    //
    // The seen-id set lives in a ref rather than being derived from `allRows`
    // inside the updater: React may invoke a state updater twice, so a set
    // rebuilt in there would double-count.
    useEffect(() => {
        // Placeholder data belongs to the PREVIOUS request. Every row in it is
        // already on screen, which is indistinguishable from "the list has
        // ended" — and concluding that here retired the sentinel before the
        // page the reader asked for had even arrived.
        if (messagesArePlaceholder) return;

        const rows = messages?.data || [];

        if (currentPage === 1) {
            seenIdsRef.current = new Set(rows.map(r => r.comm_id));
            setAllRows(rows);
            return;
        }
        const newRows = rows.filter(r => !seenIdsRef.current.has(r.comm_id));
        if (newRows.length === 0) return;
        newRows.forEach(r => seenIdsRef.current.add(r.comm_id));
        setAllRows(prev => [...prev, ...newRows]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, currentPage, cursor, messagesArePlaceholder]);

    // The server's answer, taken at face value. It used to need a local
    // override because `has_more` was an estimate over raw rows; it is now a
    // comparison of counted conversations.
    const canLoadMore = hasMore;

    const isLoadingMore = (messagesLoading || messagesFetching) && currentPage > 1;

    const loadMore = useCallback(() => {
        // Guard: don't fire if a load is already in-flight or if the backend
        // says there is nothing left. The sentinel is unmounted in both cases,
        // but an observer callback already queued from the previous render can
        // still arrive.
        if (isLoadingMore) return;
        if (!canLoadMore) return;

        // Prefer the cursor. It names the row the last window ended on, so it
        // is immune to the list shifting underneath a scroll — mail arriving
        // moves every offset down, mail deleted moves them up, and only one of
        // those merely repeats rows. The other steps over them.
        const nextCursor = messages?.next_cursor;
        if (nextCursor) {
            setCursor(nextCursor);
            setCurrentPage(page => page + 1);
            return;
        }
        // No cursor: a conversation-level sort, which has no row position to
        // resume from. Offset paging is correct there, just slower.
        setCurrentPage(page => page + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingMore, canLoadMore, messages]);

    // The accumulated rows ARE the list, in the order the server returned them.
    // Re-sorting here would reorder only what has been loaded and undo the
    // server's ordering across pages — the exact defect the server sort fixes.
    const entries = allRows;

    // ── Selection ─────────────────────────────────────────────────────────
    //
    // No `filters`/`allowEscalation` any more: selection is exactly what is
    // ticked (#710), so the hook never needs to describe the view as a
    // predicate and the unfaithful `sender` filter stops being a special case.
    const selection = useMailSelection({
        entries,
        total: totalCount,
    });

    // Reset selection when the inbox, the filters or the folder change: a
    // selection carried across a view change would act on rows the user can no
    // longer see.
    const { clear: clearSelection, buildTarget } = selection;
    // #646 — the bulk bar's Tag action (responder+, the same floor as selecting).
    const bulkTag = useBulkTagPanel({ inboxId, selection, entries });
    useEffect(() => { clearSelection(); }, [inboxId, filterKey, folder, clearSelection]);

    // ── Mail state ────────────────────────────────────────────────────────
    //
    // One handler for the row hover strip and the bulk bar: they are the same
    // verbs over different targets. `buildTarget(inboxId)` names this inbox, so
    // the backend resolves the SHARED lane and its `access_grants` role ladder
    // rather than the personal one.
    const starMail = useStarMail();
    const importantMail = useImportantMail();
    const readMail = useReadMail();
    const archiveMail = useArchiveMail();
    const snoozeMail = useSnoozeMail();
    const assignMail = useAssignMail();
    const moveMail = useMoveMail();

    // Every message the backend collapsed behind the row. Same contract as the
    // personal lane's helper; the fallback covers rows cached before the field
    // existed.
    const threadIds = useCallback(
        (item) => (item?.thread_comm_ids?.length ? item.thread_comm_ids : [item?.comm_id]).filter(Boolean),
        []
    );

    const handleBulkDelete = useCallback(async () => {
        const ids = Array.from(selection.selectedIds);
        if (!ids.length) return;
        // Split before confirming, not after: the two halves go to different
        // endpoints and the sentence the user agrees to has to be the one that
        // runs. A draft is not "removed from this inbox" — it is discarded, and
        // it was never in the inbox to begin with.
        const selected = entries.filter((row) => selection.selectedIds.has(row.comm_id));
        const { drafts } = partitionDrafts(selected);
        const draftIds = draftIdsOf(drafts);
        const messageIds = ids.filter((id) => !draftIds.includes(id));

        const parts = [];
        if (messageIds.length) {
            parts.push(`remove ${messageIds.length} message(s) from this inbox`);
        }
        if (draftIds.length) {
            parts.push(`delete ${draftIds.length} draft(s)`);
        }
        if (!window.confirm(`This will ${parts.join(' and ')}. Continue?`)) return;

        try {
            if (draftIds.length) {
                const { failed } = await deleteDrafts.mutateAsync(draftIds);
                if (failed) {
                    toast.error(`${failed} draft(s) could not be deleted`);
                } else {
                    toast.success(`${draftIds.length} draft(s) deleted`);
                }
            }
            if (messageIds.length) {
                await bulkDelete.mutateAsync(messageIds);
                toast.success(`${messageIds.length} message(s) removed from this inbox`);
            }
            clearSelection();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not remove messages');
        }
    }, [bulkDelete, deleteDrafts, entries, selection.selectedIds, clearSelection]);

    // ── Bulk Trash (#710): the two doors out of the bin, at selection scale ──
    //
    // Trash on this lane is the per-inbox REMOVAL RECORD, and its verbs are
    // per-message, manager-authorized routes — there is no bulk restore/purge
    // endpoint to reach for. So a bulk selection fans out over the existing
    // per-message hooks: bounded so the request cost is predictable, and
    // settled per id so one failure neither hides the successes (they stay
    // committed) nor keeps the failed ids in the selection by accident.
    //
    // Loaded ids only, always — the same boundary `bulkActionsFor` enforces.
    // A predicate "restore all" would have to invent ids the surface never
    // had, and the fan-out cannot know which rows the predicate matches.
    //
    // Per-message confirmations would be a dialog storm, so the whole
    // selection confirms once through the same AlertDialog the row path uses
    // (`pendingTrash`), then runs.
    const TRASH_BULK_CONCURRENCY = 5;

    /**
     * Fan a loaded-id selection out over the per-message Trash hooks.
     *
     * Bounded: `TRASH_BULK_CONCURRENCY` requests in flight, the next id
     * starting as each settles. `Promise.allSettled()` so one rejection never
     * cancels the rest — successes stay committed and only failed ids remain
     * selected.
     *
     * Returns `{ done, failed }` — the caller toasts the honest total (all
     * succeeded / partial / all failed) and keeps the failed ids selected.
     */
    const runBulkTrash = useCallback(async ({ ids, verb }) => {
        const mutate = verb === 'restore'
            ? (id) => restoreMessage.mutateAsync(id)
            : (id) => purgeMessage.mutateAsync(id);
        const failed = [];
        let done = 0;
        let cursor = 0;
        const worker = async () => {
            while (cursor < ids.length) {
                const id = ids[cursor++];
                try {
                    await mutate(id);
                    done += 1;
                } catch {
                    failed.push(id);
                }
            }
        };
        await Promise.allSettled(
            Array.from(
                { length: Math.min(TRASH_BULK_CONCURRENCY, ids.length) },
                () => worker(),
            )
        );
        return { done, failed };
    }, [restoreMessage, purgeMessage]);

    /**
     * Confirm-then-run gate for the bulk Trash verbs, through the SAME
     * AlertDialog the row path uses. One confirmation for the whole
     * selection — per-message dialogs would be a storm — and the id list is
     * snapshotted at confirm time so the dialog's promise matches what runs.
     */
    const requestBulkTrash = useCallback((verb) => {
        const ids = Array.from(selection.selectedIds);
        if (!ids.length) return;
        setPendingTrash({ ids, verb });
    }, [selection.selectedIds]);

    const confirmBulkTrash = useCallback(async () => {
        const pending = pendingTrash;
        setPendingTrash(null);
        if (!pending) return;
        const { ids, verb } = pending;
        const { done, failed } = await runBulkTrash({ ids, verb });
        if (failed.length === 0) {
            clearSelection();
            toast.success(verb === 'restore'
                ? (done === 1 ? '1 message restored' : `${done} messages restored`)
                : (done === 1 ? '1 message deleted' : `${done} messages deleted`));
            return;
        }
        // Keep ONLY the failed ids selected: the successes are committed and
        // stay removed; the failures are still in the bin and re-selecting
        // them is one click.
        for (const id of ids) {
            if (!failed.includes(id)) selection.toggleOne(id, false);
        }
        if (done === 0) {
            toast.error(verb === 'restore'
                ? 'Could not restore the selected messages'
                : 'Could not delete the selected messages');
        } else {
            toast.warning(verb === 'restore'
                ? `Restored ${done}, ${failed.length} failed — failed ones remain selected`
                : `Deleted ${done}, ${failed.length} failed — failed ones remain selected`);
        }
    }, [pendingTrash, runBulkTrash, clearSelection, selection]);

    const handleAction = useCallback(
        (key, item, { source = 'row' } = {}) => {
            // ── Bulk dispatch (#710) ─────────────────────────────────────
            //
            // The bulk bar names a DIRECTION, not a toggle — a bulk action
            // has no row to read state from, so `mailStateToggles(item)`
            // cannot decide for it. The bar sends `{ source: 'bulk' }` with
            // `item: null`; branching on that explicit argument keeps the row
            // switch below the single source of row semantics.
            if (source === 'bulk') {
                // The guard that used to sit here refused a destructive verb
                // under a predicate selection. There is no predicate selection
                // to refuse any more, and a guard against an unreachable state
                // is the clutter #710 asked to be rid of.
                const target = buildTarget(inboxId);
                // The 8 state verbs are one shared executor (bulkDispatch.js):
                // identical payloads and toast handling on both surfaces. What
                // stays here is this lane's own destructive dispatch — the
                // per-message Trash fan-out behind one confirmation.
                if (dispatchBulkStateAction(key, {
                    target,
                    mutations: { readMail, archiveMail, importantMail, snoozeMail },
                    user,
                    clearSelection,
                })) {
                    return;
                }
                switch (key) {
                    case 'delete':
                        // In Trash this is the per-message purge fan-out; the
                        // ordinary bulk-delete endpoint is NEVER used inside
                        // the bin (it writes the removal record, which the
                        // purge would then have nothing to remove).
                        if (folder === 'trash') requestBulkTrash('purge');
                        else handleBulkDelete();
                        return;
                    case 'restore':
                        requestBulkTrash('restore');
                        return;
                    default:
                        if (key === 'tag') {
                            bulkTag.openPanel();
                            return;
                        }
                        toast.error(`Unsupported bulk action: ${key}`);
                }
                return;
            }

            // Thread-wide, for the reason spelled out in `PersonalInboxSurface`:
            // a row is a COLLAPSED conversation and `comm_id` is only its root,
            // so a root-only archive left the rest behind and the row came
            // back while the toast said it had worked.
            const target = item
                ? { inbox_id: inboxId, ids: threadIds(item) }
                : buildTarget(inboxId);
            const toggles = mailStateToggles(item);
            const settle = (verb) => ({
                onSuccess: (res) => {
                    if (!item) clearSelection();
                    // The endpoint answers `{matched, modified}` and this used
                    // to read neither: `onSuccess` fires on any 2xx, so a
                    // mutation that changed NOTHING was reported exactly like
                    // one that changed forty. That is what let the root-only
                    // archive say "Archived" while the row stayed put, and it
                    // would hide the next bug of that shape just as well.
                    const modified = res?.data?.modified;
                    if (modified === 0) {
                        toast.warning('Nothing changed — the message may have moved already');
                        return;
                    }
                    toast.success(verb);
                },
                onError: (err) =>
                    toast.error(err?.response?.data?.detail || `Could not ${verb.toLowerCase()}`),
            });

            switch (key) {
                case 'reply':
                    if (item) {
                        navigate(
                            `/inbox/${inboxId}/${item.conversation_id || item.comm_id}`,
                            { state: { initialMode: 'reply' } },
                        );
                    }
                    return;
                case 'delete':
                    // In Trash this is the irreversible one. Everywhere else
                    // it is the reversible move INTO Trash, which is what the
                    // same endpoint has always done — it simply had no bin to
                    // move things into.
                    if (item) setPendingDelete({ ...item, permanent: folder === 'trash' });
                    else handleBulkDelete();
                    return;
                case 'restore':
                    if (!item) return;
                    restoreMessage.mutate(item.comm_id, {
                        onSuccess: () => toast.success('Restored to this inbox'),
                        onError: (err) => toast.error(
                            err?.response?.data?.detail || 'Could not restore the message'),
                    });
                    return;
                case 'important':
                    // The verb names the direction. Both of these said
                    // "Updated", which is true of every action on this switch
                    // and therefore tells the user nothing — and on a toggle
                    // it is the one word that cannot confirm what happened.
                    // The direction is already computed in `toggles`.
                    importantMail.mutate(
                        { target, important: toggles.important },
                        settle(toggles.important
                            ? 'Marked as important'
                            : 'Removed from important'),
                    );
                    return;
                case 'star':
                    starMail.mutate(
                        { target, starred: toggles.starred },
                        settle(toggles.starred ? 'Starred' : 'Unstarred'),
                    );
                    return;
                case 'markUnread':
                    // #626 — both directions, same as the personal lane.
                    readMail.mutate(
                        { target, read: toggles.read },
                        settle(toggles.read ? 'Marked read' : 'Marked unread'),
                    );
                    return;
                case 'archive':
                    archiveMail.mutate(
                        { target, archived: toggles.archived },
                        settle(toggles.archived ? 'Archived' : 'Moved to Inbox'),
                    );
                    return;
                case 'assign':
                    // Assigning to yourself is the one-click case the triage
                    // queue needs; picking another member is the picker's job.
                    assignMail.mutate(
                        { target, assignee_id: user?.user_id || null },
                        settle('Assigned'),
                    );
                    return;
                case 'snooze': {
                    // #626 — unsnooze is `until: null`.
                    if (toggles.unsnooze) {
                        snoozeMail.mutate({ target, until: null }, settle('Unsnoozed'));
                        return;
                    }
                    const until = resolveSnoozePreset(
                        'tomorrow',
                        new Date(),
                        resolveDisplayZone({ user }),
                    );
                    snoozeMail.mutate(
                        { target, until: until ? until.toISOString() : null },
                        settle('Snoozed'),
                    );
                    return;
                }
                case 'spam':
                    moveMail.mutate({ target, folder: 'spam' }, settle('Moved to spam'));
                    return;
                default:
                    toast.error(`Unsupported action: ${key}`);
            }
        },
        [
            inboxId, buildTarget, clearSelection, user, navigate, threadIds, bulkTag,
            handleBulkDelete, setPendingDelete, folder, restoreMessage,
            requestBulkTrash,
            starMail, importantMail, readMail, archiveMail, assignMail, snoozeMail, moveMail,
        ],
    );

    // ── Empty state ──────────────────────────────────────────────────────
    const filtersActive =
        Boolean(
            filters.search ||
                filters.sender ||
                filters.dateFrom ||
                filters.dateTo ||
                filters.mailbox ||
                tag,
        ) || filters.replied !== undefined;

    let emptyState;
    if (messagesError) {
        emptyState = (
            <InboxEmptyState
                title="Couldn't load messages"
                body={messagesError?.response?.data?.detail || messagesError.message}
                action={
                    <Button size="sm" variant="outline" onClick={() => refetchMessages()}>
                        Retry
                    </Button>
                }
            />
        );
    } else if (messagesLoading) {
        emptyState = (
            <div className="flex items-center justify-center h-48 text-stone-400">
                <Loader2 className="w-5 h-5 animate-spin" />
            </div>
        );
    } else if (filtersActive) {
        emptyState = (
            <InboxEmptyState
                title="No messages match these filters"
                action={
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setFilters({ page: 1 }); setTag(null); }}
                        data-testid="empty-clear-filters"
                    >
                        Clear filters
                    </Button>
                }
            />
        );
    } else {
        // The Inbox is "quiet"; any other view names itself (#721 audit R4).
        const copy = emptyFolderCopy(folder);
        emptyState = (
            <InboxEmptyState
                title={copy?.title ?? 'This inbox is quiet'}
                body={copy?.body}
            />
        );
    }

    // ── Render ───────────────────────────────────────────────────────────
    return (
        /* `flex-1 min-h-0`, NOT `h-full`. This surface is the SECOND child of
           the page's bounded, overflow-hidden column in pages/Inbox.jsx — the
           inbox header (mailbox switcher + actions) sits above it. `h-full` asks
           for 100% of that column's height while starting one header-height
           below its top, so the surface ended exactly one header-height past the
           column's bottom edge and `overflow-hidden` clipped the tail: the last
           row stayed cut off at the end of the scroll, taking the "Load more" /
           "Showing all N messages" footer with it. Measured against the compiled
           stylesheet at 1440x900: column clientHeight 844 over scrollHeight 901,
           surface bottom 57px below the column's, and the table's own
           overflow-y-auto never scrolled because it was handed 612px of height
           for 612px of rows. `flex-1` sizes it from the space that is actually
           left, and `min-h-0` releases the flex item's default
           `min-height: auto` so it can shrink instead of growing to its content.
           PersonalInboxSurface already roots itself the same way. */
        <div className="flex flex-1 flex-col min-h-0" data-testid="unified-inbox-surface">
            <div className="flex flex-1 flex-col min-h-0">
            {/* Compose moved INTO the control strip as its violet primary, and
                the metrics moved onto the title row as `InboxStatsInline` — both
                where §10 and the prototype put them.

                Three banners were removed with them. The context line ("member-
                level inbox · N mailboxes pooled") restated what the switcher and
                the metrics already say. The subscription-expiry warning and the
                import-status notice are real signals but neither is actionable
                from a mail list: both now live on Manage Inboxes, next to the
                Re-verify control that resolves them. */}
            <ImportStatusBanner stats={stats} />
            <InboxFiltersBar
                filters={barFilters}
                onChange={setFilters}
                inboxId={inboxId}
                distroSmtp={selectedInbox?.distro_smtp || null}
                resultCount={entries.length}
                totalCount={totalCount}
                onCompose={mayCompose ? () => navigate(`/inbox/${inboxId}/compose`) : undefined}
                onSettings={isAdmin ? () => navigate('/admin/unified-inbox') : undefined}
                onSync={handleRefresh}
                isSyncing={messagesFetching}
                canManage={canManage}
                onManageMailboxes={onManageMailboxes}
            />
            {/* Rail and list share a row BENEATH the title, control and filter
                strips, which span the full width above them — §10's shell, and
                what the prototype draws. The rail is IN FLOW: side navs push
                content at desktop widths, they never overlay. */}
            <div className="flex flex-1 min-h-0">
            <ConnectedFolderRail
                inboxId={inboxId}
                activeView={folder}
                onSelectView={selectFolder}
                activeTag={tag}
                onSelectTag={setTag}
                canTag={atLeast(role, 'responder')}
                canManageTags={Boolean(isAdmin)}
                onManageTags={() => navigate('/admin/tags')}
            />

            {/* The list gets its own COLUMN so the Trash bar spans it rather
                than sitting beside it as a third column. `min-h-0` for the
                same reason the other two carry it. */}
            <div className="relative flex-1 flex flex-col min-h-0">
            {bulkTag.panel}
            {folder === 'trash' && (
                <TrashNoticeBar
                    /* The manager floor, the same one Delete uses — NOT
                       `canManage`, which is about administering the inbox's
                       mailboxes and is a different question. */
                    onEmpty={atLeast(role, 'manager')
                        ? () => setConfirmEmptyTrash(true)
                        : undefined}
                    isEmptying={emptyTrash.isPending}
                    canEmpty={entries.length > 0}
                />
            )}
            <UnifiedInboxTable
                entries={entries}
                sortKey={urlFilters.sort}
                sortDirection={urlFilters.direction || 'desc'}
                onSort={toggleSort}
                canDelete={atLeast(role, 'manager')}
                emptyState={emptyState}
                inboxId={inboxId}
                isUnified={true}
                hasMore={canLoadMore}
                totalCount={totalCount}
                onLoadMore={loadMore}
                isLoadingMore={isLoadingMore}
                isLoading={messagesLoading}
                selection={selection}
                canSelect={atLeast(role, 'responder')}
                onAction={handleAction}
                countLabel={`${entries.length} of ${totalCount}`}
                folderLabel={folder}
            />
            </div>
            </div>

            </div>

            <AlertDialog
                open={Boolean(pendingDelete)}
                onOpenChange={open => !open && setPendingDelete(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {pendingDelete?.is_draft
                                ? `Delete draft "${pendingDelete?.subject || '(No subject)'}"?`
                                : <>
                                    {pendingDelete?.permanent ? 'Delete' : 'Move'}{' '}
                                    "{pendingDelete?.subject || '(No subject)'}"
                                    {pendingDelete?.permanent ? ' forever?' : ' to Trash?'}
                                  </>}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {/* Plain strings, not a multi-line template literal:
                                a template literal keeps its source indentation,
                                so the sentence renders with runs of whitespace
                                in the middle of it. */}
                            {pendingDelete?.is_draft
                                ? 'This cannot be undone — a draft has no Trash to go to. '
                                  + 'It was never sent, so no mailbox and no other inbox '
                                  + 'is touched.'
                                : pendingDelete?.permanent
                                ? "This inbox loses the message for good and it cannot be "
                                  + "restored here. It stays in the member's Outlook mailbox, "
                                  + "in the CRM's communication history, and in any other "
                                  + 'inbox it belongs to.'
                                : "It moves to this inbox's Trash, where you can restore it "
                                  + "for 30 days. The email stays in the member's Outlook "
                                  + "mailbox and in the CRM's communication history "
                                  + 'throughout.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="inbox-message-delete-confirm"
                            onClick={async () => {
                                const entry = pendingDelete;
                                setPendingDelete(null);
                                const permanent = Boolean(entry.permanent);
                                try {
                                    // A draft is in `mail_drafts`, so the
                                    // per-message route below resolves no
                                    // thread rows and answers 404 for it.
                                    if (entry.is_draft) {
                                        await deleteDrafts.mutateAsync(
                                            draftIdsOf([entry])
                                        );
                                        toast.success('Draft deleted');
                                    } else if (permanent) {
                                        await purgeMessage.mutateAsync(entry.comm_id);
                                        toast.success('Deleted');
                                    } else {
                                        await deleteMessage.mutateAsync(entry.comm_id);
                                        toast.success('Moved to Trash');
                                    }
                                } catch (err) {
                                    toast.error(
                                        err?.response?.data?.detail ||
                                            (entry.is_draft
                                                ? 'Could not delete the draft'
                                                : permanent
                                                  ? 'Could not delete the message'
                                                  : 'Could not move the message to Trash')
                                    );
                                }
                            }}
                        >
                            {pendingDelete?.is_draft
                                ? 'Delete draft'
                                : pendingDelete?.permanent
                                  ? 'Delete forever'
                                  : 'Move to Trash'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={Boolean(pendingTrash)}
                onOpenChange={open => !open && setPendingTrash(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {pendingTrash?.verb === 'restore'
                                ? `Restore ${pendingTrash?.ids.length ?? 0} message(s)?`
                                : `Delete ${pendingTrash?.ids.length ?? 0} message(s) forever?`}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {pendingTrash?.verb === 'restore'
                                ? 'They return to this inbox, where the team can work them again.'
                                : "This inbox loses the messages for good and they cannot be restored here. They stay in the member's Outlook mailbox, in the CRM's communication history, and in any other inbox they belong to."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="inbox-bulk-trash-confirm"
                            onClick={confirmBulkTrash}
                        >
                            {pendingTrash?.verb === 'restore' ? 'Restore' : 'Delete forever'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>


            <AlertDialog
                open={confirmEmptyTrash}
                onOpenChange={(open) => !open && setConfirmEmptyTrash(false)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Empty Trash?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Everything in this inbox's Trash is removed for good and
                            cannot be restored here. The emails stay in the members'
                            Outlook mailboxes, in the CRM's communication history, and
                            in any other inbox they belong to.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="empty-trash-confirm"
                            onClick={async () => {
                                setConfirmEmptyTrash(false);
                                try {
                                    const res = await emptyTrash.mutateAsync();
                                    const n = res?.data?.purged_rows ?? 0;
                                    toast.success(
                                        n ? `Trash emptied — ${n} removed` : 'Trash is empty'
                                    );
                                } catch (err) {
                                    toast.error(
                                        err?.response?.data?.detail ||
                                            "Couldn't empty Trash"
                                    );
                                }
                            }}
                        >
                            Empty Trash
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* InboxThreadModal and ContactEmailComposer removed — replaced by
                full-page EmailDetailPage (/inbox/:inboxId/:conversationId) and
                ComposePage (/inbox/:inboxId/compose) routes. See App.js. */}
        </div>
    );
}
